import { Link, useNavigate } from "react-router-dom";
import { Activity, LayoutDashboard, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function signIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Header({ variant = "landing" }) {
  const onLight = variant === "landing";
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/80 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 group">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue-light text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
            <Activity className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="font-heading font-semibold text-lg tracking-tight text-ink-900">
            Healthchoices<span className="text-brand-blue">.health</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-ink-600">
          <a href="/#how" className="hover:text-ink-900 transition-colors" data-testid="nav-how">How it works</a>
          <a href="/#samples" className="hover:text-ink-900 transition-colors" data-testid="nav-samples">Sample workups</a>
          <a href="/#pricing" className="hover:text-ink-900 transition-colors" data-testid="nav-pricing">Pricing</a>
        </nav>

        <div className="flex items-center gap-3">
          {onLight && !user && !loading && (
            <a
              href="/start"
              data-testid="header-cta-start"
              className="hidden sm:inline-flex items-center rounded-full bg-brand-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-brand-blue-hover active:scale-95"
            >
              Get my workup
            </a>
          )}

          {loading ? (
            <span className="h-9 w-9 rounded-full bg-slate-100 animate-pulse" />
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 pl-1 pr-3 py-1 hover:bg-surface-subtle transition-colors"
              >
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user.picture || ""} alt={user.name || user.email} />
                  <AvatarFallback className="bg-brand-blue text-white text-xs">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-ink-900 hidden sm:inline">
                  {user.name || user.email}
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel data-testid="user-menu-label" className="text-ink-500 font-normal">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-dashboard"
                  onClick={() => navigate("/dashboard")}
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" strokeWidth={2.2} />
                  My workups
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem
                    data-testid="user-menu-admin"
                    onClick={() => navigate("/admin")}
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" strokeWidth={2.2} />
                    Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-signout"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={2.2} />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={signIn}
              data-testid="signin-btn"
              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-ink-900 hover:bg-surface-subtle transition-colors"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
